// Modified CreateFarmModal.js with map drawing capability
import React, { useState, useRef, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Step,
  Stepper,
  StepLabel,
  Paper
} from '@material-ui/core';
import { withScriptjs, withGoogleMap, GoogleMap, Polygon } from 'react-google-maps';
import { DrawingManager } from 'react-google-maps/lib/components/drawing/DrawingManager';
import { makeStyles } from '@material-ui/styles';
import apiService from '../../../services/api';

// Styles remain the same with additions for map container
const useStyles = makeStyles(theme => ({
  // Existing styles...
  mapContainer: {
    height: '400px',
    width: '100%',
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${theme.palette.divider}`
  },
  stepContent: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2)
  }
}));

// Map component with drawing capability
const FarmBoundaryMap = withScriptjs(withGoogleMap(props => {
  const { onBoundaryChange } = props;
  const [drawingManager, setDrawingManager] = useState(null);
  const polygonRef = useRef(null);
  
  // Center on South Africa by default since that seems to be the focus area
  const defaultCenter = { lat: -28.4792, lng: 24.6727 }; 
  
  const handlePolygonComplete = polygon => {
    if (!polygon || !polygon.getPath) return;
    
    const coordinates = polygon.getPath().getArray().map(coord => [
      coord.lng(), 
      coord.lat()
    ]);
    
    // Make sure we have valid coordinates
    if (coordinates.length < 3) return;
    
    // Close the polygon by repeating first point
    coordinates.push([coordinates[0][0], coordinates[0][1]]);
    
    try {
      // Calculate area in hectares using the Google Maps geometry library
      const area = window.google.maps.geometry.spherical.computeArea(
        polygon.getPath()
      ) / 10000; // Convert sq meters to hectares
      
      // Update parent component with the GeoJSON and area
      onBoundaryChange({
        boundary: {
          type: 'Polygon',
          coordinates: [coordinates]
        },
        area_hectares: area.toFixed(2)
      });
      
      // Store reference to allow editing
      polygonRef.current = polygon;
      
      // Clear drawing mode but keep the polygon
      if (drawingManager) {
        drawingManager.setDrawingMode(null);
      }
    } catch (err) {
      console.error('Error processing polygon:', err);
    }
  };
  
  const onDrawingManagerLoad = ref => {
    if (ref) {
      setDrawingManager(ref);
      props.onDrawingManagerLoad(ref);
    }
  };
  
  return (
    <GoogleMap
      defaultZoom={6}
      defaultCenter={defaultCenter}
      options={{
        mapTypeId: 'satellite',
        mapTypeControl: true
      }}
    >
      {window.google && window.google.maps && (
        <DrawingManager
          onLoad={onDrawingManagerLoad}
          defaultDrawingMode={window.google.maps.drawing.OverlayType.POLYGON}
          defaultOptions={{
            drawingControl: true,
            drawingControlOptions: {
              position: window.google.maps.ControlPosition.TOP_CENTER,
              drawingModes: [
                window.google.maps.drawing.OverlayType.POLYGON,
              ],
            },
            polygonOptions: {
              fillColor: '#BFDFFF',
              fillOpacity: 0.3,
              strokeColor: '#0066FF',
              strokeWeight: 2,
              clickable: true,
              editable: true,
              zIndex: 1,
            }
          }}
          onPolygonComplete={handlePolygonComplete}
        />
      )}
    </GoogleMap>
  );
}));

const CreateFarmModal = ({ open, onClose, onFarmCreated }) => {
  const classes = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    // Farm data
    name: '',
    crop_type: '',
    area_hectares: '',
    planting_date: '',
    farm_location: '',  // Added farm location field
    farm_province: '',  // Added farm province field
    // Farmer data
    owner_name: '',
    owner_phone: '',
    owner_email: '',
    owner_location: '',
    owner_province: '',
    // Boundary data with default (will be replaced by map selection)
    boundary: {
      type: 'Polygon',
      coordinates: [[
        [32.5825, 0.3476], // Default coordinates
        [32.5925, 0.3476],
        [32.5925, 0.3376],
        [32.5825, 0.3376],
        [32.5825, 0.3476]
      ]]
    }
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [drawingManagerRef, setDrawingManagerRef] = useState(null);

  // List of common crop types
  const cropTypes = [
    'Maize', 'Rice', 'Wheat', 'Beans', 'Cassava', 'Coffee', 
    'Tea', 'Sorghum', 'Millet', 'Soybeans', 'Sugarcane', 'Cotton'
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };
  
  const handleBoundaryChange = (boundaryData) => {
    console.log('Received boundary data with location:', boundaryData);
    
    // Extract location info with fallbacks
    const locationInfo = boundaryData.locationInfo || {};
    const newLocation = locationInfo.location || '';
    const newProvince = locationInfo.province || '';
    
    console.log('Extracted location info:', { location: newLocation, province: newProvince });
    
    const updatedData = {
      ...formData,
      boundary: boundaryData.boundary,
      area_hectares: boundaryData.area_hectares,
      // Always update farm location fields (can be empty if geocoding failed)
      farm_location: newLocation,
      farm_province: newProvince,
      // Also update owner location as a convenience (can be changed by user)
      owner_location: newLocation || formData.owner_location,
      owner_province: newProvince || formData.owner_province
    };
    
    console.log('Updated form data with locations:', {
      farm_location: updatedData.farm_location,
      farm_province: updatedData.farm_province,
      owner_location: updatedData.owner_location,
      owner_province: updatedData.owner_province
    });
    
    setFormData(updatedData);
  };

  const handleDrawingManagerLoad = ref => {
    if (ref) {
      setDrawingManagerRef(ref);
    }
  };
  
  const handleNext = () => {
    setActiveStep(prevStep => prevStep + 1);
  };
  
  const handleBack = () => {
    setActiveStep(prevStep => prevStep - 1);
  };
  
  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      // Validate form data
      if (!formData.name || !formData.owner_name) {
        throw new Error('Farm name and owner name are required');
      }

      // STEP 1: Create the farmer first
      console.log('Step 1: Creating farmer...');
      const farmerData = {
        name: formData.owner_name,
        phone: formData.owner_phone || "", // Don't use hardcoded default
        email: formData.owner_email || "", // Don't use hardcoded default
        // Use location from boundary data if available, or leave blank
        location: formData.owner_location || formData.farm_location || "",
        province: formData.owner_province || formData.farm_province || ""
      };
      
      console.log('Creating farmer with data:', farmerData);
      let farmer;
      try {
        farmer = await apiService.createFarmer(farmerData);
        console.log('Farmer created successfully:', farmer);
      } catch (error) {
        console.error('Error creating farmer:', error);
        throw new Error('Failed to create farmer: ' + (error.message || 'Unknown error'));
      }

      if (!farmer || !farmer.id) {
        throw new Error('Failed to get farmer ID from server');
      }

      // STEP 2: Now create the farm with the farmer's ID
      console.log('Step 2: Creating farm with farmer ID:', farmer.id);
      
      // Log form data before creating farm to debug
      console.log('Form data before creating farm:', {
        farm_location: formData.farm_location,
        farm_province: formData.farm_province
      });
      
      // For numeric fields, convert to numbers
      // ULTRA SIMPLE APPROACH: Focus purely on getting the data structure right
      // Looking at the backend code in /app/processing/boundary.py and satellite_imagery.py:
      // - The API calls get_farm_tile_id(farm['boundary'])
      // - This function expects farm.get('boundary') or farm.get('geojson') or farm.get('location')
      
      // Create a super simple GeoJSON object for the boundary
      let geoJson;
      try {
        if (formData.boundary && formData.boundary.coordinates && formData.boundary.coordinates[0]) {
          // Make a clean copy of the coordinates
          const coords = [...formData.boundary.coordinates[0]];
          
          // Ensure polygon is closed
          if (coords[0][0] !== coords[coords.length-1][0] || coords[0][1] !== coords[coords.length-1][1]) {
            coords.push([...coords[0]]);
          }
          
          // Create a clean GeoJSON structure
          geoJson = {
            type: "Polygon",
            coordinates: [coords]
          };
        }
      } catch (e) {
        console.error('Error processing boundary:', e);
      }
      
      // Fallback boundary if we couldn't process the user's drawing
      if (!geoJson) {
        geoJson = {
          type: "Polygon",
          coordinates: [[
            [32.5825, 0.3476],
            [32.5925, 0.3476],
            [32.5925, 0.3376],
            [32.5825, 0.3376], 
            [32.5825, 0.3476]
          ]]
        };
      }
      
      // Calculate center point
      const coords = geoJson.coordinates[0];
      const centerLat = coords.reduce((sum, coord) => sum + coord[1], 0) / coords.length;
      const centerLng = coords.reduce((sum, coord) => sum + coord[0], 0) / coords.length;
      
      // Create location object with lat/lng format
      const locationObj = {
        lng: centerLng,
        lat: centerLat
      };
      
      console.log('GeoJSON:', JSON.stringify(geoJson));
      console.log('Location object:', locationObj);
      
      // Final farm data structure that should work with the backend API quirks
      const farmData = {
        name: formData.name,
        owner_id: farmer.id,
        crop_type: formData.crop_type,
        area_hectares: formData.area_hectares ? parseFloat(formData.area_hectares) : undefined,
        planting_date: formData.planting_date || undefined,
        // Text location fields for the UI
        location: formData.farm_location || "Kampala",
        province: formData.farm_province || "Central Region",
        // Try a completely different structure that provides what get_farm_tile_id expects
        boundary: {
          // This is for the GeoJSON check in get_farm_tile_id
          type: "Polygon",
          coordinates: geoJson.coordinates,
          // This is for the geojson check in get_farm_tile_id
          geojson: geoJson,
          // This is for the location check in get_farm_tile_id
          location: locationObj
        },
        // Also provide location at the top level for other functions
        geo_location: locationObj
      };
      
      // Output final data structure for debugging
      console.log('Final farm data structure:', JSON.stringify(farmData, null, 2));
      
      console.log('farmData with location info:', {
        location: farmData.location,
        province: farmData.province
      });

      // Call API to create farm
      console.log('Creating new farm with data:', farmData);
      
      let newFarm;
      try {
        // Use the API service to create the farm
        console.log('Calling API to create farm');
        newFarm = await apiService.createFarm(farmData);
        console.log('Farm created via API:', newFarm);
        
        console.log('Farm created successfully:', newFarm);
        
        // Close modal and notify parent
        onFarmCreated(newFarm);
        onClose();
      } catch (error) {
        console.error('Error from API while creating farm:', error);
        throw new Error('Failed to create farm: ' + (error.message || 'Unknown error'));
      }
    } catch (err) {
      console.error('Error creating farm:', err);
      setError(err.message || 'Failed to create farm. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Define steps for the stepper
  const steps = ['Farm Details', 'Draw Boundary', 'Owner Information', 'Review'];
  
  return (
    <Dialog open={open} onClose={onClose} aria-labelledby="create-farm-title" maxWidth="md" fullWidth>
      <DialogTitle id="create-farm-title">Create New Farm</DialogTitle>
      <DialogContent>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        
        <div className={classes.stepContent}>
          {/* Step 1: Farm Details */}
          {activeStep === 0 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="name"
                  label="Farm Name"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.name}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl variant="outlined" className={classes.formControl}>
                  <InputLabel id="crop-type-label">Crop Type</InputLabel>
                  <Select
                    labelId="crop-type-label"
                    id="crop-type"
                    name="crop_type"
                    value={formData.crop_type}
                    onChange={handleChange}
                    label="Crop Type"
                    disabled={isSubmitting}
                  >
                    <MenuItem value=""><em>Select a crop</em></MenuItem>
                    {cropTypes.map(crop => (
                      <MenuItem key={crop} value={crop}>{crop}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="planting_date"
                  label="Planting Date"
                  type="date"
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  value={formData.planting_date}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>
          )}
          
          {/* Step 2: Draw Boundary */}
          {activeStep === 1 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle1">
                  Draw your farm boundary on the map:
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Use the drawing tools to create a polygon around your farm. 
                  Click to place points and double-click to complete the shape.
                </Typography>
                <div className={classes.mapContainer}>
                  <FarmBoundaryMap
                    googleMapURL={`https://maps.googleapis.com/maps/api/js?key=${process.env.REACT_APP_GOOGLE_MAPS_API_KEY}&v=3.exp&libraries=drawing,geometry,places`}
                    loadingElement={<div style={{ height: '100%' }} />}
                    containerElement={<div style={{ height: '100%' }} />}
                    mapElement={<div style={{ height: '100%' }} />}
                    onBoundaryChange={handleBoundaryChange}
                    onDrawingManagerLoad={handleDrawingManagerLoad}
                    drawingManager={drawingManagerRef}
                  />
                </div>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  name="area_hectares"
                  label="Area (Hectares)"
                  variant="outlined"
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, step: 0.01 }}
                  value={formData.area_hectares}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  helperText="Auto-calculated from your drawing. You can adjust if needed."
                />
              </Grid>
            </Grid>
          )}
          
          {/* Step 3: Owner Information */}
          {activeStep === 2 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  name="owner_name"
                  label="Owner/Applicant Name"
                  variant="outlined"
                  fullWidth
                  required
                  value={formData.owner_name}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="owner_phone"
                  label="Phone Number"
                  variant="outlined"
                  fullWidth
                  value={formData.owner_phone}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="owner_email"
                  label="Email"
                  variant="outlined"
                  fullWidth
                  value={formData.owner_email}
                  onChange={handleChange}
                  disabled={isSubmitting}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="owner_location"
                  label="Location (Optional)"
                  variant="outlined"
                  fullWidth
                  value={formData.owner_location}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  helperText={formData.owner_location ? "Auto-populated from map boundary" : "Will use farm location if empty"}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  name="owner_province"
                  label="Province/Region (Optional)"
                  variant="outlined"
                  fullWidth
                  value={formData.owner_province}
                  onChange={handleChange}
                  disabled={isSubmitting}
                  helperText={formData.owner_province ? "Auto-populated from map boundary" : "Will use farm province if empty"}
                />
              </Grid>
              <Grid item xs={12}>
                <Paper style={{ padding: '8px', backgroundColor: '#e3f2fd' }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> Location fields should be auto-populated from your farm boundary. 
                    You can leave them blank if you want to use the same location as the farm.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
          
          {/* Step 4: Review */}
          {activeStep === 3 && (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Paper elevation={1} style={{ padding: '16px' }}>
                  <Typography variant="h6">Farm Details</Typography>
                  <Typography>Farm Name: {formData.name}</Typography>
                  <Typography>Crop Type: {formData.crop_type}</Typography>
                  <Typography>Area: {formData.area_hectares} hectares</Typography>
                  <Typography>Planting Date: {formData.planting_date || 'Not specified'}</Typography>
                  <Typography>Farm Location: {formData.farm_location || 'Not specified'}</Typography>
                  <Typography>Farm Province: {formData.farm_province || 'Not specified'}</Typography>
                  
                  <Typography variant="h6" style={{ marginTop: '16px' }}>Owner Information</Typography>
                  <Typography>Owner Name: {formData.owner_name}</Typography>
                  <Typography>Contact: {formData.owner_phone || 'Not specified'}</Typography>
                  <Typography>Email: {formData.owner_email || 'Not specified'}</Typography>
                  <Typography>Location: {formData.owner_location || 'Not specified'}</Typography>
                  <Typography>Province: {formData.owner_province || 'Not specified'}</Typography>
                  
                  <Typography variant="caption" color="textSecondary" style={{ marginTop: '16px', display: 'block' }}>
                    A farm boundary has been defined with {formData.boundary.coordinates[0].length - 1} points.
                  </Typography>
                </Paper>
              </Grid>
            </Grid>
          )}
          
          {error && (
            <Typography variant="body2" className={classes.error}>
              {error}
            </Typography>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={isSubmitting}>
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={isSubmitting}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button 
            onClick={handleNext}
            color="primary"
            variant="contained"
            disabled={isSubmitting || (activeStep === 0 && !formData.name)}
          >
            Next
          </Button>
        ) : (
          <Button 
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={isSubmitting || !formData.name || !formData.owner_name}
            startIcon={isSubmitting ? <CircularProgress size={20} /> : null}
          >
            {isSubmitting ? 'Creating...' : 'Create Farm'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CreateFarmModal;
